import { Loader2, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import useLocale from '../../contexts/LocaleContext';
import { handleApiError } from '../../lib/api-client';
import { useSaveSurveyResponse } from '../../hooks/useSurveys';
import type { SurveyAnswers, SurveyColumnDto, SurveyDto, SurveyFieldDto, SurveyResponseDto } from '../../types/api';
import { Button } from '../ui/Button';

interface Props {
  survey: SurveyDto;
  location: { id: string; name: string; locationType: string };
  response: SurveyResponseDto | null;
  readOnly?: boolean;
  onSaved?: () => void;
}

type TableRow = Record<string, string | number>;

const localized = (item: { label: string; labelKinyarwanda?: string; labelFrench?: string }, locale: string) =>
  locale === 'rw' ? item.labelKinyarwanda || item.label : locale === 'fr' ? item.labelFrench || item.label : item.label;

export default function SurveyResponseForm({ survey, location, response, readOnly = false, onSaved }: Props) {
  const { locale, dictionary } = useLocale();
  const saveMutation = useSaveSurveyResponse();
  const [answers, setAnswers] = useState<SurveyAnswers>({});

  // (Re)initialize local answers whenever the target location or stored response changes
  useEffect(() => {
    setAnswers(response?.answers ? JSON.parse(JSON.stringify(response.answers)) : {});
  }, [location.id, response?.id, response?.lastEditedAt]);

  const setFieldValue = (key: string, value: unknown) => setAnswers(prev => ({ ...prev, [key]: value }));

  const getTableRows = (field: SurveyFieldDto): TableRow[] => {
    const value = answers[field.key];
    return Array.isArray(value) ? (value as TableRow[]) : [];
  };

  const updateTableCell = (field: SurveyFieldDto, rowIndex: number, colKey: string, value: string) => {
    const rows = [...getTableRows(field)];
    rows[rowIndex] = { ...rows[rowIndex], [colKey]: value };
    setFieldValue(field.key, rows);
  };

  const addTableRow = (field: SurveyFieldDto) => setFieldValue(field.key, [...getTableRows(field), {}]);

  const removeTableRow = (field: SurveyFieldDto, rowIndex: number) =>
    setFieldValue(
      field.key,
      getTableRows(field).filter((_, i) => i !== rowIndex)
    );

  const handleSave = async () => {
    // Client-side required checks (backend re-validates everything)
    for (const field of survey.fields) {
      if (field.type === 'TABLE') {
        const rows = getTableRows(field);
        if ((field.minRows ?? 0) > rows.length) {
          toast.error(`${localized(field, locale)}: ${dictionary['surveys.min_rows_error'] || 'more rows are required'}`);
          return;
        }
        for (const [i, row] of rows.entries()) {
          for (const col of field.columns ?? []) {
            const v = row[col.key];
            if (col.required && (v === undefined || v === null || String(v).trim() === '')) {
              toast.error(`${localized(col, locale)} (${dictionary['surveys.row'] || 'row'} ${i + 1}): ${dictionary['surveys.required'] || 'required'}`);
              return;
            }
          }
        }
      } else if (field.required) {
        const v = answers[field.key];
        if (v === undefined || v === null || String(v).trim() === '') {
          toast.error(`${localized(field, locale)}: ${dictionary['surveys.required'] || 'required'}`);
          return;
        }
      }
    }

    try {
      await saveMutation.mutateAsync({ surveyId: survey.id, locationId: location.id, answers });
      toast.success(dictionary['surveys.response_saved'] || 'Response saved');
      onSaved?.();
    } catch (error) {
      handleApiError(error);
    }
  };

  const renderScalarInput = (
    type: string,
    options: string[] | undefined,
    value: unknown,
    onChange: (v: string) => void,
    compact = false
  ) => {
    const baseClass = `${compact ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm'} w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500`;
    if (type === 'SELECT') {
      return (
        <select value={(value as string) ?? ''} onChange={e => onChange(e.target.value)} disabled={readOnly} className={baseClass}>
          <option value=''>{dictionary['surveys.select_option'] || 'Select…'}</option>
          {(options ?? []).map(opt => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    }
    const inputType = type === 'NUMBER' ? 'number' : type === 'DATE' ? 'date' : 'text';
    return (
      <input
        type={inputType}
        value={(value as string) ?? ''}
        onChange={e => onChange(e.target.value)}
        disabled={readOnly}
        className={baseClass}
      />
    );
  };

  const renderField = (field: SurveyFieldDto) => {
    if (field.type === 'BOOLEAN') {
      const value = answers[field.key];
      return (
        <div className='flex gap-2'>
          {[true, false].map(option => (
            <label
              key={String(option)}
              className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer text-sm ${
                value === option ? 'border-primary bg-secondary' : 'border-gray-200'
              }`}
            >
              <input
                type='radio'
                checked={value === option}
                disabled={readOnly}
                onChange={() => setFieldValue(field.key, option)}
                className='text-primary'
              />
              {option ? dictionary['surveys.yes'] || 'Yes' : dictionary['surveys.no'] || 'No'}
            </label>
          ))}
        </div>
      );
    }

    if (field.type !== 'TABLE') {
      return renderScalarInput(field.type, field.options, answers[field.key], v =>
        setFieldValue(field.key, field.type === 'NUMBER' && v !== '' ? Number(v) : v)
      );
    }

    const rows = getTableRows(field);
    const columns = field.columns ?? [];
    return (
      <div className='space-y-2'>
        <div className='overflow-x-auto border border-gray-200 rounded-lg'>
          <table className='w-full text-xs'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='px-2 py-2 text-left font-medium text-gray-500 w-8'>#</th>
                {columns.map(col => (
                  <th key={col.key} className='px-2 py-2 text-left font-medium text-gray-600'>
                    {localized(col, locale)}
                    {col.required && <span className='text-red-500 ml-0.5'>*</span>}
                  </th>
                ))}
                {!readOnly && <th className='w-10' />}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 2} className='px-3 py-4 text-center text-gray-400 italic'>
                    {dictionary['surveys.no_rows'] || 'No entries yet'}
                  </td>
                </tr>
              )}
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex} className='border-t border-gray-100'>
                  <td className='px-2 py-1.5 text-gray-400'>{rowIndex + 1}</td>
                  {columns.map((col: SurveyColumnDto) => (
                    <td key={col.key} className='px-1.5 py-1.5'>
                      {renderScalarInput(col.type, col.options, row[col.key], v => updateTableCell(field, rowIndex, col.key, v), true)}
                    </td>
                  ))}
                  {!readOnly && (
                    <td className='px-1.5 py-1.5 text-center'>
                      <button
                        type='button'
                        onClick={() => removeTableRow(field, rowIndex)}
                        className='p-1 text-red-500 hover:bg-red-50 rounded'
                        title={dictionary['surveys.remove_row'] || 'Remove row'}
                      >
                        <Trash2 className='h-3.5 w-3.5' />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className='flex items-center justify-between'>
          {!readOnly && (
            <Button type='button' variant='outline' size='sm' onClick={() => addTableRow(field)} className='text-xs'>
              <Plus className='h-3.5 w-3.5 mr-1' />
              {dictionary['surveys.add_row'] || 'Add entry'}
            </Button>
          )}
          <span className='text-xs text-gray-500'>
            {dictionary['surveys.total_entries'] || 'Total'}: <strong>{rows.length}</strong>
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className='space-y-5'>
      {response && (
        <div className='text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-2.5 space-y-0.5'>
          <p>
            {dictionary['surveys.submitted_by'] || 'Submitted by'} <strong>{response.submittedByName || '—'}</strong>{' '}
            {new Date(response.submittedAt).toLocaleDateString()}
          </p>
          {response.lastEditedAt && (
            <p>
              {dictionary['surveys.last_edited_by'] || 'Last edited by'} <strong>{response.lastEditedByName || '—'}</strong>{' '}
              {new Date(response.lastEditedAt).toLocaleDateString()}
            </p>
          )}
        </div>
      )}

      {survey.fields.map(field => (
        <div key={field.key}>
          <label className='block text-sm font-medium text-gray-800 mb-1.5'>
            {localized(field, locale)}
            {field.required && <span className='text-red-500 ml-0.5'>*</span>}
          </label>
          {renderField(field)}
        </div>
      ))}

      {!readOnly && (
        <div className='flex justify-end pt-2 border-t border-gray-100'>
          <Button type='button' onClick={handleSave} disabled={saveMutation.isPending} size='sm'>
            {saveMutation.isPending ? (
              <>
                <Loader2 className='h-4 w-4 mr-1.5 animate-spin' />
                {dictionary['surveys.saving'] || 'Saving…'}
              </>
            ) : response ? (
              dictionary['surveys.update_response'] || 'Update response'
            ) : (
              dictionary['surveys.submit_response'] || 'Submit response'
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
